import { getApiBaseUrl } from './api';
// Use function to get API URL dynamically (detects mobile vs desktop)
const getApiBase = () => getApiBaseUrl();

// Helper to get auth token from localStorage
const getAuthToken = (): string | null => {
  try {
    const sessionData = localStorage.getItem('booking_service_auth');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      // Check if session is still valid (not expired)
      if (session.expiresAt && Date.now() <= session.expiresAt) {
        return session.token || null;
      }
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return null;
};

export interface WalletSummary {
  ledger: {
    id: string;
    balance: number;
    creditLimit?: number;
    status: string;
    mode?: 'CREDIT' | 'PREPAID';
    availableCredit?: number;
    mobilePassAccessToken?: string;
  };
  availableCredit?: number;
  lastUpdated?: string;
}

export interface WalletTransaction {
  id: string;
  type: 'DEBIT' | 'CREDIT';
  amount: number;
  description: string;
  date: string;
  reservationId?: string;
}

/** Strip embedded CUIDs from ledger memos so golfers see readable copy */
function humanizeWalletEntryDescription(raw: string | undefined | null, fallback: string): string {
  const t = (raw ?? '').trim();
  if (!t) return fallback;
  if (/^c[a-z0-9]{20,}$/i.test(t)) return fallback;
  const cleaned = t
    .replace(/\bc[a-z0-9]{20,}\b/gi, '')
    .replace(/\s*·\s*/g, ' · ')
    .replace(/[,:]\s*,/g, ',')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s*[·,:]\s*|\s*[·,:]\s*$/g, '')
    .trim();
  return cleaned.length > 0 ? cleaned : fallback;
}

export const walletApi = {
  async getCustomerSummary(customerId: string): Promise<WalletSummary> {
    try {
      console.log('🔍 Fetching wallet summary for customer:', customerId);
      const token = getAuthToken();
      console.log('🔍 Token retrieved:', token ? `Token exists (length: ${token.length})` : 'No token found');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('✅ Authorization header added to request');
      } else {
        console.warn('⚠️ No token available for wallet API request');
      }
      
      console.log('🔍 Making request to:', `${getApiBase()}/wallet/customers/${customerId}/summary`);
      const response = await fetch(`${getApiBase()}/wallet/customers/${customerId}/summary`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Wallet summary API error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 Wallet summary response:', data);
      
      // Ensure we return the data in the expected format
      // API returns: { hasAccount: boolean, ledger?: { id, mode, status, balance, availableCredit?, creditLimit? } }
      if (!data.ledger) {
        return {
          ledger: {
            id: '',
            balance: 0,
            status: 'active'
          },
          availableCredit: 0,
          lastUpdated: new Date().toISOString()
        };
      }
      
      return {
        ledger: {
          id: data.ledger.id || '',
          balance: data.ledger.balance || 0,
          creditLimit: data.ledger.creditLimit,
          status: data.ledger.status || 'active',
          mode: data.ledger.mode,
          availableCredit: data.ledger.availableCredit,
          mobilePassAccessToken: data.ledger.mobilePassAccessToken,
        },
        availableCredit: data.ledger.availableCredit,
        lastUpdated: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error fetching wallet summary:', error);
      throw error;
    }
  },

  async getCustomerTransactions(customerId: string, limit: number = 1000): Promise<WalletTransaction[]> {
    try {
      console.log('🔍 Fetching wallet transactions for customer:', customerId, 'limit:', limit);
      const token = getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBase()}/wallet/customers/${customerId}/transactions?limit=${limit}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Wallet transactions API error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 Wallet transactions response:', data);
      console.log('🔍 Total transactions available:', data.total);
      console.log('🔍 Transactions returned:', data.entries?.length || 0);
      
      // API returns { entries: [...], total: number }
      // Map entries to WalletTransaction format
      const transactions: WalletTransaction[] = (data.entries || []).map((entry: any) => {
        // Format date properly
        const dateStr = entry.postedAt || entry.occurredAt;
        const date = dateStr ? (typeof dateStr === 'string' ? dateStr : dateStr.toISOString()) : new Date().toISOString();
        
        // Extract reservation ID from externalRef if it exists
        let reservationId: string | undefined;
        if (entry.externalRef && typeof entry.externalRef === 'string') {
          const match = entry.externalRef.match(/reservation-(.+)/);
          if (match) {
            reservationId = match[1];
          }
        }
        
        const memoOrCode = entry.memo || entry.code || '';
        return {
          id: entry.id,
          type: entry.side === 'DEBIT' ? 'DEBIT' : 'CREDIT',
          amount: entry.amount || 0,
          description: humanizeWalletEntryDescription(memoOrCode, entry.code ? String(entry.code) : 'Transaction'),
          date: date,
          reservationId: reservationId
        };
      });
      
      // If we got less than the total and less than our limit, there might be more
      if (data.total > transactions.length && transactions.length === limit) {
        console.warn(`⚠️ Only fetched ${transactions.length} of ${data.total} total transactions. Consider implementing pagination.`);
      }
      
      return transactions;
    } catch (error) {
      console.error('Error fetching wallet transactions:', error);
      throw error;
    }
  }
};
